import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import {
  SendOTPRequest,
  VerifyOTPRequest,
  OTPResponse,
  VerifyOTPResponse,
  GetUserProfileResponse,
  GetUserRoleResponse,
  GetSetupStatusResponse,
} from '../api/types';

// User type for provider web portal
const USER_TYPE = 'provider';

/**
 * Auth service hook for handling authentication operations
 * Provides sendOTP, verifyOTP, and resendOTP functionality
 */
export const useAuthService = () => {
  /**
   * Send OTP to phone number
   * @param phoneNumber - Phone number without country code
   * @param countryCode - Country code with + prefix (e.g., +91)
   * @returns OTP response with expiry time
   */
  const sendOTP = useCallback(
    async (phoneNumber: string, countryCode: string): Promise<OTPResponse> => {
      const payload: SendOTPRequest = {
        phone_number: phoneNumber,
        country_code: countryCode,
        platform: 'web',
      };

      const response = await axiosInstance.post<OTPResponse>(
        API_ENDPOINTS.SEND_OTP,
        payload
      );

      return response.data;
    },
    []
  );

  /**
   * Verify OTP and get authentication tokens
   * @param phoneNumber - Phone number without country code
   * @param countryCode - Country code with + prefix
   * @param otpCode - 6-digit OTP code
   * @returns Verification response with tokens and user data
   */
  const verifyOTP = useCallback(
    async (
      phoneNumber: string,
      countryCode: string,
      otpCode: string
    ): Promise<VerifyOTPResponse> => {
      const payload: VerifyOTPRequest = {
        phone_number: phoneNumber,
        country_code: countryCode,
        otp_code: otpCode,
        user_type: USER_TYPE,
      };

      const response = await axiosInstance.post<VerifyOTPResponse>(
        API_ENDPOINTS.VERIFY_OTP,
        payload
      );

      return response.data;
    },
    []
  );

  /**
   * Resend OTP (uses same sendOTP endpoint)
   */
  const resendOTP = sendOTP;

  /**
   * Store login details in localStorage
   */
  const setLoginDetails = useCallback(
    (accessToken: string, refreshToken: string, user: VerifyOTPResponse['data']['user']) => {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    },
    []
  );

  /**
   * Clear login details from localStorage
   */
  const clearLoginDetails = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = useCallback((): boolean => {
    return !!localStorage.getItem('accessToken');
  }, []);

  /**
   * Get user profile from API
   * Updates localStorage with the latest profile data
   */
  const getUserProfile = useCallback(async (): Promise<GetUserProfileResponse> => {
    const response = await axiosInstance.get<GetUserProfileResponse>(
      API_ENDPOINTS.GET_USER_PROFILE
    );

    // Update localStorage with latest profile data
    if (response.data.success && response.data.data) {
      const existingUser = localStorage.getItem('user');
      if (existingUser) {
        try {
          const user = JSON.parse(existingUser);
          const updatedUser = {
            ...user,
            display_name: response.data.data.display_name,
            profile_image_url: response.data.data.profile_image_url,
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch {
          // Ignore parse errors
        }
      }
    }

    return response.data;
  }, []);

  /**
   * Get user role from API
   */
  const getUserRole = useCallback(async (): Promise<GetUserRoleResponse> => {
    const response = await axiosInstance.get<GetUserRoleResponse>(
      API_ENDPOINTS.GET_MY_ROLE
    );

    // Update localStorage with role data
    if (response.data.success && response.data.data) {
      const existingUser = localStorage.getItem('user');
      if (existingUser) {
        try {
          const user = JSON.parse(existingUser);
          const updatedUser = {
            ...user,
            role: {
              role_name: response.data.data.role_name,
              role_display_name: response.data.data.role_display_name,
              is_creator: response.data.data.is_creator,
              is_provider: response.data.data.is_provider,
              business_id: response.data.data.business_id,
              business_name: response.data.data.business_name,
              permissions: response.data.data.permissions,
            },
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch {
          // Ignore parse errors
        }
      }
    }

    return response.data;
  }, []);

  /**
   * Get setup status from API
   * Checks if user has business and team setup
   */
  const getSetupStatus = useCallback(async (): Promise<GetSetupStatusResponse> => {
    const response = await axiosInstance.get<GetSetupStatusResponse>(
      API_ENDPOINTS.GET_SETUP_STATUS
    );
    return response.data;
  }, []);

  return {
    sendOTP,
    verifyOTP,
    resendOTP,
    setLoginDetails,
    clearLoginDetails,
    isAuthenticated,
    getUserProfile,
    getUserRole,
    getSetupStatus,
  };
};
